import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useContext, useEffect, useState } from "react";
import Badge from "../../components/elements/Badge";
import DeleteModal from "../../components/common/DeleteModal";
import CategoryMangeDrawer from "../../components/sections/admin/Category/CategoryMangeDrawer";
import Table from "../../components/elements/table/Table";
import { setTitle } from "../../helpers/MetaTag";
import { useDebounce } from "../../hooks/useDebounce";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import { getPaginationRange, handleFilterChange } from "../../helpers";
import TableControls from "../../components/elements/TableControls";
import { status_filter } from "../../components/constant/constant";
import {
  applicationDelete,
  applicationList,
  applicationStatusChange,
} from "../../services/applicationService";
import ReactPortal from "../../components/common/ReactPortal";
import StatusChangeTooltip from "../../components/common/StatusChangeTooltip";
import { X } from "lucide-react";
import { Download, FileText, Image as ImageIcon } from "lucide-react";

import { useNavigate, useSearchParams } from "react-router-dom";
import { postList } from "../../services/postService";
import SocketContext, { useSocket } from "../../providers/SocketContext";

import Button from "../../components/form/Button";

const Applications = () => {
  setTitle("Job Application");
  const [searchParams, setSearchParams] = useSearchParams();
  const jobIdFromUrl = searchParams.get("jobId");
  const navigate = useNavigate();
  const [selectedItems, setSelectedItems] = useState([]);
  const [activeTooltip, setActiveTooltip] = useState(null);

  const [isExporting, setIsExporting] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [modalOpen, setModalOpen] = useState({ isOpen: false, data: null });
  const [keyword, setKeyword] = useState("");
  const search = useDebounce(keyword, 300);
  const queryClient = useQueryClient();
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    data: null,
  });
  const [filters, setFilters] = useState({
    limit: 10,
    page: 1,
    sortQuery: "-createdAt",
    ...(jobIdFromUrl && { job: [jobIdFromUrl] }),
  });

  const handleBadgeClick = (e, id) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltipPosition({
      top: rect.bottom + window.scrollY + 8,
      left: rect.left + window.scrollX,
    });
    setActiveTooltip((prev) => (prev === id ? null : id));
  };

  const removeJobFilter = () => {
    setFilters((prev) => {
      const newFilters = { ...prev };
      delete newFilters.job;
      return { ...newFilters, page: 1 };
    });

    // Remove jobId from URL
    searchParams.delete("jobId");
    setSearchParams(searchParams);
  };

  const { data, isLoading, updateData, isFetching } = useQuery({
    queryKey: ["standard", filters, search],
    queryFn: async () => {
      const { job = [], status = [], ...restFilters } = filters || {};

      const queryParams = {
        ...restFilters,
        ...(search && { keyword: search }),
        jobMode: "standard",

        // MULTI JOB PARAMS
        ...Object.fromEntries(job.map((val, i) => [`job[${i}]`, val])),

        // OPTIONAL: if status ever becomes multiselect
        ...Object.fromEntries(status.map((val, i) => [`status[${i}]`, val])),
      };

      const res = await applicationList(queryParams);

      if (res?.status !== 200) {
        toast.warning(res?.message);
      }

      return res?.docs;
    },
  });

  const { data: jobList } = useQuery({
    queryKey: ["job-list"],
    queryFn: async () => {
      const res = await postList({
        limit: 10000,
        // type: "standard-job",
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.docs;
    },
  });

  const filteredJobTitle = jobList?.data?.find(
    (item) => item?._id === jobIdFromUrl,
  )?.title;

  //  status change handler =================>

  const { mutate: statusChangeHandler, isPending: statusChangeInPending } =
    useMutation({
      mutationFn: async ({ id, status }) => {
        queryClient.setQueryData(["standard", filters, search], (state) => {
          if (!state) return;

          const updatedData = state?.data?.map((item) =>
            item?._id === id
              ? {
                  ...item,
                  status: status,
                }
              : item,
          );

          return { ...state, data: updatedData };
        });
        const res = await applicationStatusChange({ ids: [id], status });

        return { res, payload: { id, status } };
      },

      onSuccess: ({ res, payload }) => {
        if (res?.status === 200) {
          toast.success("Job Department status change successfully.");
        } else {
          queryClient.setQueryData(["standard", filters, search], (state) => {
            if (!state) return;

            const revertedData = state?.data?.map((item) =>
              item?._id === payload.id
                ? {
                    ...item,
                    status: item.status,
                  }
                : item,
            );

            return { ...state, data: revertedData };
          });

          toast.error(res?.message || "Failed to update status.");
        }
      },

      onError: (error) => {
        toast.error(
          error?.message || "Something went wrong while updating status.",
        );
        console.error("Status update error:", error);
      },
    });
  useSocket(
    "jobApplyResponse",
    (newApplication) => {
      const { ApplicationData } = newApplication;

      if (!ApplicationData) return;

      const normalizedApp = {
        ...ApplicationData,
        jobDetail: ApplicationData.jobDetail || {
          content: { en: { title: "-" } },
        },
        applicant: ApplicationData.applicant || {
          firstName: "-",
          email: "-",
          phone: {},
        },
        attachments: ApplicationData.attachments || [],
      };

      // Update React Query cache
      queryClient.setQueryData(
        ["standard", filters, search],
        (existingData) => {
          if (!existingData) return existingData;

          const existingApps = existingData.data || [];

          const alreadyExists = existingApps.some(
            (app) => app._id === normalizedApp._id,
          );
          if (alreadyExists) return existingData;

          return {
            ...existingData,
            data: [normalizedApp, ...existingApps],
            metadata: {
              ...existingData.metadata,
              totalDocs: (existingData.metadata?.totalDocs || 0) + 1,
            },
          };
        },
      );
    },
    [filters, search],
  );

  const tableHeadData = [
    {
      _id: 1,
      name: "Job",
      align: "left",
      isFilter: true,
      onFilter: (val) => {
        if (val?.length > 0) {
          setFilters((pre) => ({
            ...pre,
            job: val.map((item) => item.value),
            page: 1,
          }));
        } else {
          setFilters((pre) => {
            const updated = { ...pre };
            delete updated.job;
            return { ...updated, page: 1 };
          });
        }
      },

      filterData:
        jobList?.data?.map((item) => {
          return {
            name: item?.content?.en?.title,
            value: item?._id,
            type: "checkbox",
          };
        }) || [],
    },
    {
      _id: 2,
      name: "Applicant",
      align: "left",
    },
    {
      _id: 3,
      name: "Applicant Email",
      align: "left",
    },
    {
      _id: 3.5,
      name: "Mobile No.",
      align: "left",
    },
    {
      _id: 4,
      name: "Resume",
      align: "left",
    },
    {
      _id: 5,
      name: "Apllied On",
      align: "left",
      isSort: true,
      onSort: (val) => {
        if (val === "DESC") {
          setFilters((pre) => ({ ...pre, sortQuery: "-createdAt" }));
        } else {
          setFilters((pre) => ({ ...pre, sortQuery: "createdAt" }));
        }
      },
    },

    {
      _id: 6,
      name: "Status",
      align: "left",
      isFilter: false,
      isFilterSearch: false,
      onFilter: (val) => {
        if (val?.length > 0 && val?.length === 1) {
          setFilters((pre) => ({
            ...pre,
            status: val?.map((item) => item?.value),
            page: 1,
          }));
        } else {
          setFilters((pre) => {
            delete pre?.status;
            return { ...pre };
          });
        }
      },
      filterData: status_filter || [],
    },
    // {
    //   _id: 8,
    //   name: "Action",
    //   align: "left",
    // },
  ];

  const statusArray = [
    {
      state: "submitted",
      color: "info",
    },
    {
      state: "reviewed",
      color: "indigo",
    },
    {
      state: "shortlisted",
      color: "warning",
    },
    {
      state: "hired",
      color: "success",
    },
    {
      state: "rejected",
      color: "danger",
    },
    {
      state: "withdrawn",
      color: "stone",
    },
  ];
  const isImageFile = (ext = "") => {
    return ["jpg", "jpeg", "png", "webp", "gif"].includes(ext.toLowerCase());
  };
  const downloadFile = async (url, filename = "attachment") => {
    try {
      const res = await fetch(url, { mode: "cors" });
      const blob = await res.blob();

      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      toast.error("Unable to download file");
    }
  };

  // Table Body Data =====================>
  const tableData = data?.data?.map((item) => {
    return {
      _id: item?._id,
      rowData: [
        {
          _id: 1,
          type: "text",
          align: "left",
          cellClass: "!min-w-[150px]",
          data: item?.jobDetail?.content?.en?.title || "-",
        },
        {
          _id: 2,
          type: "data",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: item?.applicant?.firstName || "-",
        },
        {
          _id: 3,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: item?.applicant?.email || "-",
        },
        {
          _id: 3.5,
          type: "text",
          align: "left",
          cellClass: "!min-w-[120px]",
          data: item?.applicant?.phone
            ? item.applicant.phone?.fullNumber
              ? item.applicant.phone?.fullNumber
              : `${item.applicant.phone.countryCode ?? ""} ${item.applicant.phone.number ?? ""}`
            : "-",
        },
        {
          _id: 4,
          type: "data",
          align: "left",
          cellClass: "!min-w-[200px]",
          data: (
            <div className="flex items-center gap-3">
              {item?.attachments?.slice(0, 1).map((file) => {
                const isImage = isImageFile(file?.extension);

                return (
                  <div
                    key={file?._id}
                    className="
              flex items-center gap-3
              transition-all group
            "
                  >
                    {/* REAL IMAGE PREVIEW */}
                    {isImage ? (
                      <div className="relative w-14 h-14 rounded-md overflow-hidden border">
                        <img
                          src={file.url}
                          alt={file.filename}
                          className="w-full h-full object-cover"
                        />

                        {/* Download overlay */}
                        <div
                          onClick={() => downloadFile(file.url, file.filename)}
                          className="
                    absolute inset-0 bg-black/40
                    flex items-center justify-center
                    opacity-0 group-hover:opacity-100
                    transition-opacity cursor-pointer
                  "
                        >
                          <Download size={20} className="text-white" />
                        </div>
                      </div>
                    ) : (
                      /* NON-IMAGE (PDF / DOC) */
                      <div
                        onClick={() => downloadFile(file.url, file.filename)}
                        className="
                  flex items-center gap-3 px-3 py-2
                  border rounded-md cursor-pointer bg-white
                "
                      >
                        <FileText size={22} className="text-primary" />
                        <div className="flex flex-col">
                          <span className="text-sm font-medium truncate max-w-[120px]">
                            {file.filename}
                          </span>
                          <span className="text-xs text-gray-500 uppercase">
                            {file.extension}
                          </span>
                        </div>
                        <Download size={18} className="ml-auto text-gray-400" />
                      </div>
                    )}
                  </div>
                );
              })}

              {item?.attachments?.length > 1 && (
                <span className="ml-2 text-xs font-semibold px-2 py-1 rounded-full bg-gray-100 text-gray-700 border">
                  +{item.attachments.length - 1}
                </span>
              )}
            </div>
          ),
        },

        {
          _id: 5,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: item?.createdAt
            ? dayjs(item?.createdAt).format("DD MMM, YYYY")
            : "-",
        },

        {
          _id: 5,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: (
            <div className="relative">
              <div
                className="flex gap-2 items-center cursor-pointer"
                onClick={(e) => handleBadgeClick(e, item._id)}
              >
                <Badge
                  type={
                    statusArray.find((st) => st.state === item?.status)?.color
                  }
                  label={
                    statusArray.find((st) => st.state === item?.status)?.state
                  }
                />
              </div>

              {activeTooltip === item._id && (
                <ReactPortal
                  position={tooltipPosition}
                  onClose={() => setActiveTooltip(null)}
                >
                  {statusArray.map((status) => {
                    return (
                      <StatusChangeTooltip
                        statusItem={status}
                        itemId={item._id}
                        statusChange={statusChangeHandler}
                        setActiveTooltip={setActiveTooltip}
                        isActive={item.status === status.state}
                      />
                    );
                  })}
                </ReactPortal>
              )}
            </div>
          ),
        },
        // {
        //   _id: 6,
        //   type: "action",
        //   align: "left",
        //   cellClass: "w-[5%]",
        //   actionData: [
        //     {
        //       _id: 3, // make sure this _id is unique in the action array
        //       name: "View Applications",
        //       icon: "fa-regular fa-eye", // Eye icon for viewing
        //       onClick: () => {
        //         navigate(`/admin/application-view/${item?.uid}`);
        //       },
        //       standout: false,
        //     },
        //     {
        //       _id: 4,
        //       name: "Delete",
        //       icon: "fa-regular fa-trash-can",
        //       standout: true,
        //       onClick: () => {
        //         setDeleteModal({
        //           isOpen: true,
        //           data: item,
        //         });
        //       },
        //     },
        //   ],
        // },
      ],
    };
  });

  // add or edit Response =================>
  async function taxonomyManageResponse(res) {
    if (res?.status === 200 || res?.status === 201) {
      if (res?.status === 201) {
        if (filters?.page === 1) {
          queryClient.setQueryData(["department", filters, search], (state) => {
            if (state) {
              let stateData = JSON.parse(JSON.stringify(state?.data));
              stateData = [...stateData, res?.data];
              return { ...state, data: stateData };
            }
          });
          await queryClient.refetchQueries(["department", filters, search]);
        } else {
          setFilters((pre) => ({ ...pre, page: 1 }));
        }
      }

      if (res?.status === 200) {
        queryClient.setQueryData(["department", filters, search], (state) => {
          if (state) {
            let stateData = JSON.parse(JSON.stringify(state?.data));
            stateData = stateData?.map((item) =>
              item?._id === res?.data?._id ? { ...item, ...res?.data } : item,
            );
            return { ...state, data: stateData };
          }
        });
        await queryClient.refetchQueries(["department", filters, search]);
      }

      setModalOpen({ isOpen: false, data: null });
    } else {
      toast.warning(res?.message || res?.error);
    }
  }

  // category delete handler =================>
  const { mutate: deleteHandler, isPending: deletePending } = useMutation({
    mutationFn: async (payload) => {
      const res = await applicationDelete({
        ids: Array.isArray(payload) ? payload : [payload],
      });
      return { res, payload: Array.isArray(payload) ? payload : [payload] };
    },
    onSuccess: ({ res, payload }) => {
      if (res?.status === 200) {
        setDeleteModal({ isOpen: false, data: null });
        queryClient.setQueryData(["standard", filters, search], (state) => {
          if (state) {
            let stateData = JSON.parse(JSON.stringify(state?.data));
            stateData = (stateData || [])?.filter(
              (item) => !payload?.includes(item?._id),
            );
            return { ...state, data: stateData };
          }
        });
        toast.success("Application deleted successfully.");

        if (data?.metadata?.totalDocs > filters?.limit) {
          queryClient.invalidateQueries(["standard", filters, search]);
        }
      } else {
        toast.warning(res?.message || res?.error);
      }
    },
    onError: (error) => {
      console.log(error);
      toast.error(
        error?.message || "Something went wrong while delete department.",
      );
    },
  });
  const { socket } = useContext(SocketContext);
  const handleExport = async () => {
    setIsExporting(true);

    try {
      const { job = [], status = [], ...restFilters } = filters || {};

      const exportParams = {
        ...restFilters,
        // limit: data?.metadata?.totalDocs || 1000,
        limit: 1000,
        page: 1,
        ...(search && { keyword: search }),
        jobMode: "standard",
        ...Object.fromEntries(job.map((val, i) => [`job[${i}]`, val])),
        ...Object.fromEntries(status.map((val, i) => [`status[${i}]`, val])),
      };

      const res = await applicationList(exportParams);
      const exportData = res?.docs?.data || [];

      const formattedData = exportData.map((item) => ({
        "Job Title": item?.jobDetail?.content?.en?.title || "-",
        "Applicant Name": `${item?.applicant?.firstName || ""} ${
          item?.applicant?.lastName || ""
        }`.trim(),
        "Applicant Email": item?.applicant?.email || "-",
        Phone:
          item?.applicant?.phone?.countryCode && item?.applicant?.phone?.number
            ? `${item.applicant.phone.countryCode} ${item.applicant.phone.number}`
            : "-",
        // "Applied On": item?.createdAt
        //   ? dayjs(item.createdAt).format("DD MMM, YYYY")
        //   : "-",
        Status: item?.status || "-",
        Organization: item?.organization?.content?.en?.title || "-",
      }));

      // Convert to CSV manually
      const headers = Object.keys(formattedData[0] || {});
      const csvRows = [];

      csvRows.push(headers.join(","));

      formattedData.forEach((row) => {
        const values = headers.map(
          (header) => `"${(row[header] || "").toString().replace(/"/g, '""')}"`,
        );
        csvRows.push(values.join(","));
      });

      const csvString = csvRows.join("\n");

      const blob = new Blob([csvString], {
        type: "text/csv;charset=utf-8;",
      });

      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);

      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `applications_${dayjs().format("YYYY-MM-DD")}.csv`,
      );

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Export failed:", error);
      toast.error("Failed to export applications.");
    } finally {
      setIsExporting(false);
    }
  };

  // useEffect(() => {
  //   socket?.emit("hello", { id: 1 });
  // });
  return (
    <div>
      <div className="space-y-4 w-full flex flex-col pb-10">
        <div className="space-y-8">
          <div className="flex items-center gap-2"></div>

          <div className="flex items-center gap-3">
            <TableControls
              limit={filters?.limit}
              totalDocs={data?.metadata?.totalDocs}
              onLimitChange={(val) =>
                handleFilterChange("limit", val, setFilters)
              }
              onSearch={(val) => {
                setKeyword(val);
                handleFilterChange("page", 1, setFilters);
              }}
            />
            <Button
              buttonFunction={handleExport}
              isDisable={isExporting || !data?.metadata?.totalDocs}
              buttonClasses="px-8"
              buttonLabel={isExporting ? "Exporting..." : "Export CSV"}
              buttonIcon="fas fa-download"
            />
            {jobIdFromUrl && filteredJobTitle && (
              <div className="inline-flex items-center px-4 py-1 bg-emerald-100 text-emerald-800 rounded-full text-base font-medium hover:bg-emerald-200 transition-colors">
                <span className="mr-3">{filteredJobTitle}</span>
                <button
                  onClick={removeJobFilter}
                  className="flex-shrink-0 ml-1 h-5 w-5 rounded-full inline-flex items-center justify-center hover:bg-emerald-300 focus:outline-none focus:bg-emerald-300"
                >
                  <X size={14} />
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="space-y-4">
          {isLoading ? (
            <p className="text-center py-10">Loading applications...</p>
          ) : data?.data?.length === 0 ? (
            <p className="text-center py-10 text-gray-500">
              No applications found
            </p>
          ) : (
            data?.data?.map((item) => (
              <div
                key={item?._id}
                className="mt-4 border border-gray-300 shadow-[0_0_8px_rgba(0,0,0,0.1)] rounded-xl p-3 flex items-center justify-between hover:shadow-md transition"
              >
                {/* LEFT SIDE */}
                <div className="flex items-center gap-3">
                  {/* Applicant Avatar (or fallback initial) */}
                  <img
                    src={item?.organization?.logo?.url}
                    alt="company"
                    className="w-14 h-14 rounded object-cover"
                  />

                  <div>
                    {/* Job Title */}
                    <h4 className="text-base font-semibold text-gray-800">
                      {item?.jobDetail?.content?.en?.title || "-"}
                    </h4>

                    {/* Applicant Name */}
                    <p className="text-xs text-gray-700 font-medium">
                      Job by {item?.organization?.content?.en?.title || "-"}
                    </p>
                    <p className="text-xs text-gray-700 font-medium">
                      {item?.applicant?.firstName || "-"}
                    </p>
                    {/* Email */}
                    <p className="text-xs text-gray-700 font-medium">
                      {item?.applicant?.email || "-"}
                    </p>
                  </div>
                </div>

                {/* RIGHT SIDE */}
                <div className="flex items-center gap-3">
                  {/* STATUS BADGE */}
                  <div
                    className="cursor-pointer"
                    onClick={(e) => handleBadgeClick(e, item._id)}
                  >
                    <Badge
                      type={
                        statusArray.find((st) => st.state === item?.status)
                          ?.color
                      }
                      label={item?.status}
                    />
                  </div>

                  {/* VIEW BUTTON */}
                  {/* <Button
            buttonLabel="View job"
            buttonClasses="bg-white text-palatte hover:bg-transparent hover:text-white px-4 h-8 rounded-full border-palatte/50 border hover:bg-palatte2 font-medium"
            buttonLabelClasses="text-sm"
            buttonFunction={() =>
              navigate(`/admin/application-view/${item?.uid}`)
            }
          /> */}
                </div>
              </div>
            ))
          )}
        </div>
        {data?.metadata?.totalPages > 1 && (
          <div className="flex justify-center mt-6">
            <div className="flex gap-2">
              {Array.from(
                { length: data?.metadata?.totalPages },
                (_, index) => index + 1,
              ).map((page) => (
                <button
                  key={page}
                  onClick={() => handleFilterChange("page", page, setFilters)}
                  className={`px-3 py-1 rounded-md border ${
                    filters.page === page
                      ? "bg-palatte text-white"
                      : "bg-white text-gray-700"
                  }`}
                >
                  {page}
                </button>
              ))}
            </div>
          </div>
        )}

        <CategoryMangeDrawer
          open={modalOpen?.isOpen}
          data={modalOpen?.data}
          close={() => setModalOpen({ isOpen: false, data: null })}
          getResponse={taxonomyManageResponse}
          type={"department"}
          title={"Job Skills"}
        />
        <DeleteModal
          open={deleteModal?.isOpen}
          loading={deletePending}
          onClose={() =>
            setDeleteModal((pre) => ({ data: null, isOpen: false }))
          }
          deleteHandler={() => {
            deleteHandler(deleteModal?.data?._id);
          }}
        />
      </div>
    </div>
  );
};

export default Applications;
